import { createContext, ReactNode, useContext, useState, useRef } from 'react'
import Axios from 'axios'
import {
  ServiceHistory,
  VehicleCondition,
  PartexValuation,
  PartexVehicleVariant,
} from '../interfaces'
import { isPast } from 'date-fns'

const PART_EX_STORAGE_KEY = 'persist:partex'
const MOT_EXPIRY_STORAGE_KEY = 'persist:partex:motExpiry'

export type GetVariantsResponse = {
  correlationId: string
  variants: PartexVehicleVariant[]
  motOdometerValue?: number
  motCompletedDate?: string
  motExpiryDate: string
}

export type CreatePartExValuationParams = {
  correlationId: string
  variantId: string
  mileage: number
  serviceHistory: ServiceHistory
  vehicleCondition: VehicleCondition
  outstandingFinanceInPence?: number
  vrm: string
  emailAddress?: string
  isLeadGeneratingValuation?: boolean
  hasOptedInToMarketing?: boolean
  consumer?: 'web' | 'app' | 'internal'
  marketingOptInType?: 'soft' | 'hard'
}

export type ConsumerValuationsContext = {
  valuation?: PartexValuation
  motExpiryDate?: Date | null
  getVariants: (vrm: string) => Promise<GetVariantsResponse>
  createValuation: (
    params: CreatePartExValuationParams,
  ) => Promise<PartexValuation>
  clearValuation: () => void
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const ConsumerValuationsContext =
  createContext<ConsumerValuationsContext>(null as any)

type Props = {
  consumerValuationsBaseUrl: string
  children: ReactNode
}

const persistValuation = (valuation: PartexValuation) => {
  // NOTE: Double parse needed to match what older implementations - APP - inject into localStorage, left over from using Redux
  const payload = JSON.stringify({ valuation: JSON.stringify(valuation) })

  window.localStorage.setItem(PART_EX_STORAGE_KEY, payload)
}

const retrievePersistedValuation = () => {
  try {
    const str = window.localStorage.getItem(PART_EX_STORAGE_KEY)

    if (!str) {
      return
    }

    const data = JSON.parse(str) as { valuation: string }

    if (!data.valuation) {
      return
    }

    const valuation = JSON.parse(data.valuation)

    const isExpired = isPast(valuation.expiresAt * 1000)

    if (isExpired) {
      return
    }

    return valuation
  } catch {
    return
  }
}

const clearPersistedValuation = () =>
  window.localStorage.removeItem(PART_EX_STORAGE_KEY)

const persistMotExpiryDate = (motExpiryDate: Date) => {
  window.localStorage.setItem(
    MOT_EXPIRY_STORAGE_KEY,
    JSON.stringify({ motExpiryDate: motExpiryDate.toISOString() }),
  )
}

const retrievePersistedMotExpiryDate = (): Date | null => {
  try {
    const str = window.localStorage.getItem(MOT_EXPIRY_STORAGE_KEY)

    if (!str) {
      return null
    }

    const data = JSON.parse(str) as { motExpiryDate: string }

    if (!data.motExpiryDate) {
      return null
    }

    return new Date(data.motExpiryDate)
  } catch {
    return null
  }
}

const clearPersistedMotExpiryDate = () =>
  window.localStorage.removeItem(MOT_EXPIRY_STORAGE_KEY)

export const ConsumerValuationsProvider = ({
  consumerValuationsBaseUrl,
  children,
}: Props) => {
  const ctx = useContext(ConsumerValuationsContext)
  const [valuation, setValuation] = useState<PartexValuation | undefined>(
    retrievePersistedValuation,
  )
  const initialMotExpiryDate = retrievePersistedMotExpiryDate()
  const [motExpiryDate, setMotExpiryDateState] = useState<Date | null>(
    initialMotExpiryDate,
  )
  const motExpiryDateRef = useRef<Date | null>(initialMotExpiryDate)

  const context: ConsumerValuationsContext = {
    valuation,
    motExpiryDate,
    getVariants: async (vrm) => {
      const { data } = await Axios.get<GetVariantsResponse>(
        `${consumerValuationsBaseUrl}/variants/${vrm}`,
      )

      if (data.motExpiryDate) {
        const expiryDate = new Date(data.motExpiryDate)
        motExpiryDateRef.current = expiryDate
      } else {
        motExpiryDateRef.current = null
      }

      return data
    },
    createValuation: async (props) => {
      const { data } = await Axios.post(
        `${consumerValuationsBaseUrl}/valuations`,
        {
          correlationId: props.correlationId,
          variantId: props.variantId,
          mileage: props.mileage,
          serviceHistory: props.serviceHistory,
          vehicleCondition: props.vehicleCondition,
          outstandingFinanceInPence: props.outstandingFinanceInPence,
          vrm: props.vrm,
          isLeadGeneratingValuation: props.isLeadGeneratingValuation,
          emailAddress: props.isLeadGeneratingValuation
            ? props.emailAddress
            : undefined,
          hasOptedInToMarketing: props.hasOptedInToMarketing,
          consumer: props.consumer,
          marketingOptInType: props.marketingOptInType,
        },
      )

      persistValuation(data)

      if (motExpiryDateRef.current) {
        persistMotExpiryDate(motExpiryDateRef.current)
      }

      setValuation(data)
      setMotExpiryDateState(motExpiryDateRef.current)

      return data
    },
    clearValuation: () => {
      clearPersistedValuation()
      clearPersistedMotExpiryDate()
      setValuation(undefined)
      setMotExpiryDateState(null)
      motExpiryDateRef.current = null
    },
  }

  // NB: Turns this provider into a singleton
  if (ctx) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>
  }

  return (
    <ConsumerValuationsContext.Provider value={context}>
      {children}
    </ConsumerValuationsContext.Provider>
  )
}

export const useConsumerValuations = () => useContext(ConsumerValuationsContext)
